import { useEffect } from 'react';

/**
 * Lightweight SEO helper. Sets document.title and updates/creates meta tags
 * without any extra dependency. Also handles canonical + Open Graph.
 */
export default function SEO({
  title,
  description,
  canonical,
  ogImage = 'https://betterfenceanddeck.org/gallery/01-hero-dusk-pool.jpg',
  jsonLd, // optional object or array
  noindex = false,
}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const setMeta = (name, content, attr = 'name') => {
      if (!content) return () => {};
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute('content');
      el.setAttribute('content', content);
      return () => {
        if (created) el.remove();
        else if (prev != null) el.setAttribute('content', prev);
      };
    };

    const setCanonical = (href) => {
      if (!href) return () => {};
      let el = document.head.querySelector('link[rel="canonical"]');
      const created = !el;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        document.head.appendChild(el);
      }
      const prev = el.getAttribute('href');
      el.setAttribute('href', href);
      return () => {
        if (created) el.remove();
        else if (prev != null) el.setAttribute('href', prev);
      };
    };

    const setJsonLd = (data) => {
      if (!data) return () => {};
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-seo-injected', 'true');
      el.textContent = JSON.stringify(data);
      document.head.appendChild(el);
      return () => el.remove();
    };

    const cleanups = [
      setMeta('robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large'),
      setMeta('description', description),
      setMeta('og:title', title, 'property'),
      setMeta('og:description', description, 'property'),
      setMeta('og:image', ogImage, 'property'),
      setMeta('og:url', canonical, 'property'),
      setMeta('twitter:title', title),
      setMeta('twitter:description', description),
      setMeta('twitter:image', ogImage),
      setCanonical(canonical),
      setJsonLd(jsonLd),
    ];

    return () => {
      document.title = prevTitle;
      cleanups.forEach((fn) => fn && fn());
    };
  }, [title, description, canonical, ogImage, noindex, JSON.stringify(jsonLd)]);

  return null;
}
